/**
 * Single entry path for "investigate from shell" navigation.
 * Shares detectInputType with the Workbench omnibar so Header / Dashboard
 * never diverge on address vs tx vs UPI classification.
 */
import { toast } from 'sonner';
import { detectInputType, resolveOmnibarChainHint } from './WorkbenchSearch';

export type InvestigateNavigate = (to: string) => void;

export type SubmitInvestigationResult =
    | { ok: true; path: string; kind: 'tx' | 'address' | 'upi' | 'phone' }
    | { ok: false; reason: 'empty' | 'unknown' };

/**
 * Classify raw user input and build a Workbench URL, or surface a toast on failure.
 * Caller navigates with the returned path when ok.
 */
export type SubmitInvestigationOptions = {
    silent?: boolean;
    pickedChain?: string | null;
};

export function submitInvestigationQuery(
    raw: string,
    options?: SubmitInvestigationOptions,
): SubmitInvestigationResult {
    const val = raw.trim();
    if (!val) return { ok: false, reason: 'empty' };

    const detected = detectInputType(val);

    if (detected.type === 'unknown') {
        if (!options?.silent) {
            if (/^0x[a-fA-F0-9]+$/i.test(val) && val.length !== 42 && val.length !== 66) {
                toast.error(`Unrecognized input: ${val.length} hex chars. EVM address = 42, tx hash = 66.`);
            } else if (detected.label === 'Incomplete Address') {
                toast.error('Incomplete address — keep pasting or check the length.');
            } else {
                toast.error('Unrecognized address or transaction format. Please check the input.');
            }
        }
        return { ok: false, reason: 'unknown' };
    }

    const chain = resolveOmnibarChainHint(detected, options?.pickedChain);
    const identifier = detected.query || val;
    if (detected.isTx) {
        if (!options?.silent) {
            toast.info(`Detected transaction hash — opening tx view on ${chain}`);
        }
        return {
            ok: true,
            kind: 'tx',
            path: `/workbench?chain=${encodeURIComponent(chain)}&tx=${encodeURIComponent(identifier)}`,
        };
    }
    if (detected.type === 'upi_id') {
        return {
            ok: true,
            kind: 'upi',
            path: `/workbench?upi=${encodeURIComponent(val)}`,
        };
    }
    if (detected.type === 'phone') {
        return {
            ok: true,
            kind: 'phone',
            path: `/workbench?phone=${encodeURIComponent(val)}`,
        };
    }

    return {
        ok: true,
        kind: 'address',
        path: `/workbench?chain=${encodeURIComponent(chain)}&address=${encodeURIComponent(identifier)}`,
    };
}

/** Navigate to Workbench after classification. Returns whether navigation was attempted. */
export function navigateInvestigationQuery(
    raw: string,
    navigate: InvestigateNavigate,
    options?: SubmitInvestigationOptions,
): boolean {
    const result = submitInvestigationQuery(raw, options);
    if (!result.ok) return false;
    navigate(result.path);
    return true;
}
